import { endpoints } from '../api';
import { EmptyState, ErrorState, LoadingState, Note, Page, Section, Stat, Stats, Status, activityName, currency, dateTime, humanize, number } from '../components';
import { useApiQuery } from '../hooks';
import { BusinessDailyFlow, BusinessOverview } from './business';
import { MiningDailyVolume } from './mining-chart';

function remaining(end: string | null | undefined, now = Date.now()) {
  if (!end) return '—';
  const minutes = Math.round((new Date(end).getTime() - now) / 60000);
  if (minutes <= 0) return 'Fin';
  const hours = Math.floor(minutes / 60);
  return hours >= 24 ? `${Math.floor(hours / 24)}d ${hours % 24}h` : `${hours}h ${String(minutes % 60).padStart(2, '0')}m`;
}

export function TodayPage() {
  const overview = useApiQuery(endpoints.overview);
  const pilots = useApiQuery(endpoints.characters);
  const jobs = useApiQuery(endpoints.industryJobs);
  const opportunities = useApiQuery(endpoints.opportunities);
  const batches = useApiQuery(endpoints.batches);

  const data = overview.data;
  const unsold = batches.data?.filter((batch) => batch.outputQuantity > batch.soldQuantity) ?? [];
  const activeJobs = (jobs.data ?? [])
    .filter((job) => ['active', 'ready', 'paused'].includes(job.status))
    .sort((a, b) => Number(b.status === 'ready') - Number(a.status === 'ready') || (a.endDate ?? '').localeCompare(b.endDate ?? ''));
  const ranked = [...(opportunities.data ?? [])]
    .filter((item) => item.ownedBlueprint || item.candidateSource === 'owned')
    .sort((a, b) => Number(b.eligible) - Number(a.eligible) || (b.iskPerSlotHour ?? -Infinity) - (a.iskPerSlotHour ?? -Infinity))
    .slice(0, 6);
  const ready = data?.industryJobsReady ?? 0;

  return <Page title="Resumen">
    {overview.loading && !data && <LoadingState />}
    {overview.error && <ErrorState error={overview.error} onRetry={() => void overview.reload()} />}
    <Stats>
      <Stat label="Wallet" value={currency(data?.walletBalance)} />
      <Stat label="Invertible" value={currency(data?.investableISK)} />
      <Stat label="Jobs activos" value={number(data?.industryJobsActive)} />
      <Stat label="Para entregar" value={number(data?.industryJobsReady)} tone={ready > 0 ? 'warn' : undefined} />
      <Stat label="Órdenes activas" value={number(data?.marketOrdersActive)} />
      <Stat label="Lotes sin vender" value={batches.data ? number(unsold.length) : '—'} />
    </Stats>
    {data && data.alerts.length > 0 && data.alerts.map((alert) => <Note key={alert.id} tone={alert.severity === 'critical' ? 'error' : 'warn'}>
      <strong>{alert.title}.</strong> {alert.detail} <a className="link" href={alert.id.startsWith('sync') || /datos|sincroniza|ESI/i.test(alert.title) ? '#/sync' : '#/finance'}>Abrir</a>
    </Note>)}
    <div className="split split--even">
      <MiningDailyVolume />
      <BusinessDailyFlow />
    </div>
    <div className="split">
      <div className="stack" style={{ gap: 14 }}>
        <Section title="Jobs" action={<a className="link" href="#/industry/jobs">Todos</a>}>
          {jobs.loading && !jobs.data && <LoadingState />}
          {jobs.error && <ErrorState error={jobs.error} onRetry={() => void jobs.reload()} />}
          {jobs.data && activeJobs.length === 0 && <EmptyState title="Sin jobs en curso" />}
          {activeJobs.length > 0 && <div className="table-wrap"><table>
            <thead><tr><th>Producto</th><th>Actividad</th><th className="num">Runs</th><th className="num">Restante</th><th>Estado</th></tr></thead>
            <tbody>{activeJobs.slice(0, 10).map((job) => <tr key={job.id}>
              <td><strong>{job.productName}</strong></td>
              <td>{activityName(job.activity)}</td>
              <td className="num">{number(job.runs)}</td>
              <td className="num" title={dateTime(job.endDate)}>{remaining(job.endDate)}</td>
              <td><Status value={job.status} /></td>
            </tr>)}</tbody>
          </table></div>}
        </Section>
        <Section title="Mejores lotes con tus blueprints" action={<a className="link" href="#/market">Oportunidades</a>}>
          {opportunities.loading && !opportunities.data && <LoadingState />}
          {opportunities.error && <ErrorState error={opportunities.error} onRetry={() => void opportunities.reload()} />}
          {opportunities.data && ranked.length === 0 && <EmptyState title="Sin blueprints propios que comparar" />}
          {ranked.length > 0 && <div className="table-wrap"><table>
            <thead><tr><th>Producto</th><th className="num">Runs</th><th className="num">Profit</th><th className="num">ISK/h slot</th><th className="num">Capital</th><th>Estado</th></tr></thead>
            <tbody>{ranked.map((item) => <tr key={item.blueprintTypeId}>
              <td><strong>{item.typeName}</strong></td>
              <td className="num">{number(item.batchSize)}</td>
              <td className={`num ${(item.netProfit ?? 0) < 0 ? 'neg' : ''}`}>{currency(item.netProfit)}</td>
              <td className={`num ${(item.iskPerSlotHour ?? 0) < 0 ? 'neg' : ''}`}>{currency(item.iskPerSlotHour)}</td>
              <td className="num">{currency(item.capitalRequired)}</td>
              <td><Status value={item.eligible ? 'elegible' : 'bloqueado'} label={item.eligible ? 'Viable' : 'Bloqueado'} /></td>
            </tr>)}</tbody>
          </table></div>}
        </Section>
      </div>
      <div className="stack" style={{ gap: 14 }}>
        <BusinessOverview />
        <Section title="Pilotos" action={<a className="link" href="#/sync">Sincronización</a>}>
          {pilots.loading && !pilots.data && <LoadingState />}
          {pilots.error && <ErrorState error={pilots.error} onRetry={() => void pilots.reload()} />}
          {pilots.data?.length === 0 && <EmptyState title="Ningún piloto conectado" />}
          {pilots.data && <ul className="feed">{pilots.data.map((pilot) => <li key={pilot.id}>
            <strong>{pilot.name}</strong>
            <small>{pilot.corporationName ?? 'Corp sin resolver'} · {humanize(pilot.status)}</small>
            <span className="feed-end">{dateTime(pilot.lastSyncAt)}</span>
          </li>)}</ul>}
        </Section>
      </div>
    </div>
  </Page>;
}
